/**
 * googleTranslate.js — small helper that drives the hidden Google Translate
 * widget (mounted from index.html → #google_translate_element).
 *
 * Why a helper, not a full component?
 *   • Google's TranslateElement only mounts once globally. The visible UI
 *     is driven by React, but the actual translation logic is shared
 *     between (a) the global LanguageSwitcher and (b) the per-page
 *     "Translate this page" button — so it lives in this lib.
 *
 * Mechanism:
 *   1. We set the `googtrans` cookie at the host AND root domain. Google
 *      reads this on next page load to determine source/target language.
 *   2. For instant in-page translation (no reload), we also locate the
 *      hidden <select class="goog-te-combo"> and dispatch a `change`
 *      event with the target language. Works once the widget has finished
 *      booting (see waitForCombo()).
 *
 * Unsupported languages (e.g. Ndebele 'nd') silently no-op — UI keeps the
 * static react-i18next translation only.
 */

// Google Translate supports these among our portal languages.
// Ndebele (nd) is intentionally absent — not supported by Google Translate.
const GOOGLE_SUPPORTED = new Set([
  'en', 'fr', 'pt', 'sw', 'zu', 'sn',
  'af', 'ar', 'de', 'es', 'nl', 'it', 'xh', 'st',
]);

const COOKIE_NAME = 'googtrans';

const setCookie = (name, value, domain) => {
  const base = `${name}=${value};path=/;max-age=31536000;SameSite=Lax`;
  document.cookie = domain ? `${base};domain=${domain}` : base;
};

const clearCookie = (name, domain) => {
  const base = `${name}=;path=/;max-age=0`;
  document.cookie = domain ? `${base};domain=${domain}` : base;
};

/**
 * Apply Google Translate to the current page.
 * @param {string} lang — target ISO code ('en', 'fr', ...). 'en' resets.
 */
export const setGoogleTranslateLanguage = (lang) => {
  if (typeof document === 'undefined') return;
  if (!lang || !GOOGLE_SUPPORTED.has(lang)) {
    // Unsupported: reset to source so UI stays consistent.
    clearGoogleTranslate();
    return;
  }
  if (lang === 'en') {
    clearGoogleTranslate();
    return;
  }
  const value = `/en/${lang}`;
  // Set on multiple scopes so it sticks across subdomain refreshes.
  const host = window.location.hostname;
  setCookie(COOKIE_NAME, value);
  setCookie(COOKIE_NAME, value, host);
  // Also set on the eTLD+1 if present (covers ".example.com" cases)
  const parts = host.split('.');
  if (parts.length >= 2) {
    setCookie(COOKIE_NAME, value, '.' + parts.slice(-2).join('.'));
  }
  // Persist user intent
  try { localStorage.setItem('googleTranslateLang', lang); } catch (_e) { /* storage blocked */ }
  // Drive the combo for instant in-page translation (no reload).
  triggerComboChange(lang);
};

/** Remove the translation overlay (back to source English). */
export const clearGoogleTranslate = () => {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname;
  clearCookie(COOKIE_NAME);
  clearCookie(COOKIE_NAME, host);
  const parts = host.split('.');
  if (parts.length >= 2) {
    clearCookie(COOKIE_NAME, '.' + parts.slice(-2).join('.'));
  }
  try { localStorage.removeItem('googleTranslateLang'); } catch (_e) { /* storage blocked */ }
  // Reset combo
  triggerComboChange('');
};

const triggerComboChange = (lang) => {
  waitForCombo((combo) => {
    combo.value = lang || '';
    combo.dispatchEvent(new Event('change'));
  });
};

const waitForCombo = (cb, attempts = 0) => {
  const el = document.querySelector('select.goog-te-combo');
  if (el) { cb(el); return; }
  if (attempts > 40) return; // give up after ~8s
  setTimeout(() => waitForCombo(cb, attempts + 1), 200);
};

/** Read currently-applied Google Translate target language. */
export const getCurrentGoogleTranslateLang = () => {
  try {
    const stored = localStorage.getItem('googleTranslateLang');
    if (stored) return stored;
  } catch (_e) { /* ignore */ }
  const match = document.cookie.match(/googtrans=\/en\/([a-z-]+)/i);
  return match ? match[1] : 'en';
};

export const isGoogleTranslateSupported = (lang) => GOOGLE_SUPPORTED.has(lang);
